import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { formatARS } from "../lib/site";

const Row = ({ k, v }) => (v ? <><dt className="text-[var(--muted-ink)]">{k}</dt><dd className="font-semibold">{v}</dd></> : null);
const day = (iso) => new Date(iso).toLocaleDateString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" });

const Status = ({ q, busy, onAccept }) => {
  if (q.accepted_at) return <p data-testid="quote-accepted" className="flex items-start gap-2 rounded-2xl bg-[var(--lime)]/40 p-4 text-sm font-semibold"><CheckCircle2 className="h-5 w-5 shrink-0 text-[var(--forest)]" /> Aceptaste este presupuesto el {day(q.accepted_at)}. Te vamos a contactar para confirmar la reserva de la unidad.</p>;
  if (q.status !== "presupuesto") return <p data-testid="quote-closed" className="rounded-2xl bg-black/5 p-4 text-sm">Este presupuesto ya no se puede aceptar. Comunicate con nosotros respondiendo el email.</p>;
  if (q.expired) return <p data-testid="quote-expired" className="rounded-2xl bg-amber-100 p-4 text-sm">Este presupuesto venció el {day(q.valid_until)}. Pedinos uno actualizado respondiendo el email.</p>;
  return (
    <div className="space-y-2">
      <button onClick={onAccept} disabled={busy} data-testid="quote-accept-btn" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--forest)] text-base font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto sm:px-8">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Aceptar presupuesto</button>
      <p className="text-xs text-[var(--muted-ink)]">Válido hasta el {day(q.valid_until)}. Al aceptarlo, te confirmamos la reserva de la unidad.</p>
    </div>
  );
};

export default function Presupuesto() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/quotes/${token}`).then((r) => setQ(r.data)).catch((e) => setErr(apiError(e))); }, [token]);
  const accept = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/quotes/${token}/accept`); setQ(data); toast.success("¡Presupuesto aceptado!"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (err) return <p data-testid="quote-public-error" className="mx-auto max-w-xl px-5 py-24 text-center">{err}</p>;
  if (!q) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const load = q.module === "cargas" ? (q.weight_kg ? `${Number(q.weight_kg).toLocaleString("es-AR")} kg` : "") : (q.passengers ? `${q.passengers} pasajeros` : "");
  return (
    <section data-testid="quote-public-page" className="mx-auto max-w-2xl px-5 pb-20 pt-28 md:pt-36">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--forest)]">Presupuesto {q.module === "cargas" ? "de carga" : "de turismo"} · Nº {q.code}</p>
      <h1 className="mt-3 text-4xl font-bold leading-tight sm:text-5xl">{q.origin} → {q.destination}</h1>
      <dl className="mt-8 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 rounded-3xl bg-white p-6 text-sm shadow-sm">
        <Row k="Cliente" v={q.client} />
        <Row k="Fecha" v={`${q.date}${q.return_date ? ` al ${q.return_date}` : ""}`} />
        <Row k="Unidad" v={q.unit} />
        <Row k="Detalle" v={q.detail} />
        <Row k={q.module === "cargas" ? "Carga" : "Grupo"} v={load} />
        <Row k="Kilómetros" v={`${Number(q.km).toLocaleString("es-AR")} km`} />
      </dl>
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 rounded-3xl bg-[var(--ink)] p-6 text-white">
        <span className="text-sm">Precio total</span>
        <span data-testid="quote-public-price" className="text-3xl font-bold text-[var(--lime)]">{formatARS(q.price)} <span className="text-sm font-normal text-white">+ impuestos</span></span>
      </div>
      <div className="mt-6"><Status q={q} busy={busy} onAccept={accept} /></div>
    </section>
  );
}
